import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BulbOutlined, PhoneOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { ROUTES } from '@utils/constants';
import { getStudentsByParentId } from '../../api/user';
import { searchSubscriptionsByGranteeIds, type SubscriptionV2Summary } from '../../api/payment';
import { useAuth } from '../../context/AuthContext';
import { AppHeader } from '@components/layout/AppHeader';
import { StudentCard } from '../../components/dashboard/StudentCard';
import { SkeletonCard } from '../../components/ui/SkeletonLoader';
import { InfoCard } from '@components/ui/InfoCard';
import { useToastContext } from '../../context/ToastContext';
import { clearStudentSession, saveStudentSession } from '@utils/sessionStorage';
import type { Student } from '../../api/user';
import './styles/dashboard.css';
import '@styles/loader.css';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout, parentId } = useAuth();
  const { addToast } = useToastContext();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionV2Map, setSubscriptionV2Map] = useState<Record<string, SubscriptionV2Summary>>({});

  useEffect(() => {
    if (parentId) {
      fetchStudents(parentId);
    } else {
      setLoading(false);
    }
  }, [parentId]);

  const fetchStudents = async (id: string) => {
    try {
      setLoading(true);
      const response = await getStudentsByParentId(id);
      if (response.students) {
        setStudents(response.students);
      }

      // Fetch V2 subscriptions by student IDs directly (covers cases where subscription
      // is not linked to parent subscriberId)
      const studentIds = (response.students || []).map((s) => s.id).filter(Boolean) as string[];
      if (studentIds.length > 0) {
        try {
          const v2Result = await searchSubscriptionsByGranteeIds(studentIds);
          const allSubscriptions = v2Result.subscriptions || [];
          const map: Record<string, SubscriptionV2Summary> = {};

          // For each student, pick the payable subscription.
          // Prefer PENDING_PAYMENT (e.g. replacement after reactivate) over ACTIVE so
          // installment Pay CTAs surface the new sub instead of the cancelled one's successor gap.
          for (const student of response.students || []) {
            if (student.id) {
              const studentSubscriptions = allSubscriptions.filter(
                (sub) =>
                  sub.granteeId === student.id &&
                  (sub.status === 'ACTIVE' || sub.status === 'PENDING_PAYMENT'),
              );

              const sortedSubs = [...studentSubscriptions].sort(
                (a, b) =>
                  ((b as any).updatedAt ?? b.createdAt ?? 0) -
                  ((a as any).updatedAt ?? a.createdAt ?? 0),
              );
              const match =
                sortedSubs.find((sub) => sub.status === 'PENDING_PAYMENT') ??
                sortedSubs.find((sub) => sub.startDate === student.startDate) ??
                sortedSubs[0];

              if (match) {
                map[student.id] = match;
              }
            }
          }
          setSubscriptionV2Map(map);
        } catch (err) {
          console.warn('Could not fetch V2 subscriptions:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleAddStudent = () => {
    // Clear session and create new one for new student flow
    clearStudentSession();
    
    // Initialize new session with isNewStudent: true
    saveStudentSession({
      isNewStudent: true,
      formData: {
        givenName: '',
        familyName: '',
        gender: '',
        grade: { value: '', label: '' },
        campus: { id: '', name: '', location: undefined },
        shift: { id: '', name: '' },
        geoAddress: '',
      },
      createdAt: Date.now(),
    });
    
    navigate(ROUTES.STUDENT_DETAILS);
  };

  const handlePhoneClick = () => {
    const supportNumber = import.meta.env.VITE_PARENT_SUPPORT_NUMBER;
    if (!supportNumber) {
      addToast({
        type: 'error',
        message: 'Unable to contact support right now. Please try again later.',
      });
      return;
    }
    window.location.href = `tel:${supportNumber}`;
  };

  const handleCompletePayment = (studentId: string, campusData?: any, shiftData?: any) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return;

    try {
      let transportType = "";
      if (student?.tags?.transportType === 'AC') {
        transportType = 'ac';
      } else if (student?.tags?.transportType === 'NON_AC') {
        transportType = 'non-ac';
      } else {
        transportType = ""
      }
      let tripType = student?.tags?.tripType || "";


      // Ensure campus data has the required fields for displaying address
      // The campusData from getCampusesByIds should have contact.address
      const fullCampusData = campusData ? {
        id: campusData.id,
        name: campusData.name,
        location: campusData.location,
        contact: campusData.contact,
      } : { id: '', name: '', location: undefined };

      // Save session data using passed campus and shift data
      saveStudentSession({
        studentId: student.id,
        formData: {
          givenName: student.name?.split(' ')[0] || '',
          familyName: student.name?.split(' ').slice(1).join(' ') || '',
          gender: (student as any).gender || "",
          grade: { value: student.grade || '', label: student.grade || '' },
          campus: fullCampusData,
          shift: shiftData || undefined,
          geoAddress: student.geoAddress || '',
        },
        pickupLocation: student.location ? {
          coordinates: {
            lat: student.location.lat || 0,
            lng: student.location.lng || 0,
          },
          address: student.geoAddress || '',
        } : undefined,
        isNewStudent: false,
        transportType,
        startDate: student?.startDate,
        tripType,
      });

      // Navigate to review details page
      navigate(ROUTES.REVIEW_DETAILS, {
        state: {
          studentId: student.id,
          student,
          formData: {
            givenName: student.name?.split(' ')[0] || '',
            familyName: student.name?.split(' ').slice(1).join(' ') || '',
            gender: (student as any).gender || "",
            grade: { value: student.grade || '', label: student.grade || '' },
            campus: fullCampusData,
            shift: shiftData,
          },
          pickupLocation: student.location ? {
            coordinates: {
              lat: student.location.lat || 0,
              lng: student.location.lng || 0,
            },
            address: student.geoAddress || '',
          } : undefined,
          transportType,
          startDate: student?.startDate,
          tripType,
        },
      });
    } catch (error) {
      console.error('Error navigating to review details:', error);
      addToast({
        type: 'error',
        message: 'Failed to load review details. Please try again.',
      });
    }
  };

  // LAM-150: Handle installment payment from StudentCard banner
  const handlePayInstallment = (params: {
    studentId: string;
    subscriptionId: string;
    installmentNumber: number;
    amountRupees: number;
    campusData?: any;
    shiftData?: any;
    subscriptionPreview?: any;
    usedTransactionIds?: string[];
  }) => {
    const student = students.find(s => s.id === params.studentId);
    if (!student) return;

    try {
      // Attach campus and shift data to student object for SubscriptionSummaryCard
      const enrichedStudent = {
        ...student,
        campus: params.campusData,
        shift: params.shiftData,
      };

      // Navigate to UserDetails page with installment payment information
      navigate(ROUTES.USER_DETAILS, {
        state: {
          studentId: student.id,
          student: enrichedStudent,
          isInstallmentPayment: true,
          installmentPayment: {
            subscriptionId: params.subscriptionId,
            installmentNumber: params.installmentNumber,
            amountRupees: params.amountRupees,
          },
          subscriptionPreview: params.subscriptionPreview,
          usedTransactionIds: params.usedTransactionIds ?? [],
          formData: {
            givenName: student.name?.split(' ')[0] || '',
            familyName: student.name?.split(' ').slice(1).join(' ') || '',
            gender: (student as any).gender || '',
            grade: { value: student.grade || '', label: student.grade || '' },
            campus: params.campusData || { id: '', name: '', location: undefined },
            shift: params.shiftData || undefined,
            geoAddress: student.geoAddress || '',
          },
          pickupLocation: student.location ? {
            coordinates: {
              lat: student.location.lat || 0,
              lng: student.location.lng || 0,
            },
            address: student.geoAddress || '',
          } : undefined,
        },
      });
    } catch (error: any) {
      console.error('[Dashboard] handlePayInstallment error:', error);
      addToast({
        type: 'error',
        message: 'Failed to navigate to payment page. Please try again.',
      });
    }
  };

  return (
    <div className="dashboard-page">
      {/* Header */}
      <AppHeader showLogout onLogout={handleLogout} />

      {/* Main Content */}
      <div className="dashboard-page__container">
        <div className="dashboard-page__content">
          {/* Title Section with Logout */}
          <div className="dashboard-page__title-section">
            <div className="dashboard-page__title-header">
              <div>
                <h1 className="dashboard-page__title">Your Dashboard</h1>
                <p className="dashboard-page__subtitle">
                  Manage your child's transport in one place
                </p>
              </div>
            </div>
          </div>

          {/* Quick Action Row */}
          {!loading && (
            <div className="dashboard-page__quick-row">
              <button
                className="dashboard-page__quick-btn"
                onClick={() => navigate(ROUTES.FAQ)}
              >
                <QuestionCircleOutlined style={{ fontSize: 16 }} /> FAQ
              </button>
            </div>
          )}

          {/* Students List */}
          {loading ? (
            <div className="dashboard-page__students">
              <SkeletonCard count={2} />
            </div>
          ) : students.length > 0 ? (
            <div className="dashboard-page__students">
              {students.map((student) => (
                <StudentCard
                  key={student.id}
                  student={student}
                  onCompletePayment={handleCompletePayment}
                  onPayInstallment={handlePayInstallment}
                  subscriptionV2Data={subscriptionV2Map[student.id || '']}
                />
              ))}
            </div>
          ) : null}

          {/* Did You Know Card - Using InfoCard Component - Hide when loading */}
          {!loading && (
            <InfoCard
              icon={<BulbOutlined style={{ fontSize: 24, color: '#ffffff' }} />}
              title="Did you know?"
              description="You can add multiple children and manage all their transport needs from this dashboard"
              className="dashboard-info-card"
            />
          )}

          {/* Add Student Button - Only show if less than 5 students and not loading */}
          {!loading && students.length < 5 && (
            <button
              className="dashboard-page__add-student-btn"
              onClick={handleAddStudent}
            >
              ➕ {'Create Subscription'}
            </button>
          )}

          {/* Phone Support Button */}
          <button
            className="dashboard-page__phone-btn"
            onClick={handlePhoneClick}
            title="Call us for support"
          >
            <PhoneOutlined style={{ fontSize: '28px', color: '#ffffff', transform: 'rotate(90deg)' }} />
          </button>
        </div>
      </div>
    </div>
  );
};
